import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { formatMediumDate } from '../../lib/format.js';

const STATUS_LABELS = {
    draft: 'Draft',
    'company-signed': 'Awaiting your signature',
    executed: 'Signed by both parties',
    external: 'Signed on paper',
    voided: 'Voided',
    terminated: 'Ended',
};

const STATUS_COLORS = {
    draft: 'bg-secondary',
    'company-signed': 'bg-warning text-dark',
    executed: 'bg-success',
    external: 'bg-success',
    voided: 'bg-light text-dark',
    terminated: 'bg-light text-dark',
};

/**
 * The contractor's own Statements of Work.
 *
 * Numbering is per contractor and spans both the SOWs generated here and the
 * ones signed on paper before the system existed, so this is the only place the
 * sequence is visible as a whole. An SOW with an attached document — every
 * external one — can be downloaded, which is the reason migration 028 stores it.
 */
class SOWsView extends BaseComponent {
    static properties = {
        _sows: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        table { font-size: 0.9rem; }
        .status-badge { font-size: 0.75rem; }
    `;

    constructor() {
        super();
        this._sows = [];
        this._loading = true;
        this._error = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        await this._load();
    }

    async _load() {
        this._loading = true;
        this._error = null;
        try {
            const data = await api.get('/api/sows');
            this._sows = data.sows || [];
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _download(sow) {
        this._error = null;
        try {
            const { url } = await api.get(`/api/sows/${sow.id}/download`);
            window.open(url, '_blank', 'noopener');
        } catch (err) {
            this._error = err.message;
        }
    }

    render() {
        if (this._loading) {
            return html`<div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>`;
        }

        return html`
            <div class="page-header"><h4>My Statements of Work</h4></div>
            <p class="page-subtitle">
                Every SOW you have signed with Dice Driven Design, numbered in one sequence — including
                any signed on paper before this system existed.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : nothing}

            ${
                this._sows.length === 0
                    ? html`<p class="text-muted">No Statements of Work yet.</p>`
                    : html`
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th>No.</th>
                            <th>Status</th>
                            <th>Signed</th>
                            <th class="text-end">Document</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._sows.map(
                            (s) => html`
                            <tr>
                                <td class="fw-medium">${s.number}</td>
                                <td>
                                    <span class="badge status-badge ${STATUS_COLORS[s.status] || 'bg-secondary'}">
                                        ${STATUS_LABELS[s.status] || s.status}
                                    </span>
                                </td>
                                <td class="text-muted">
                                    ${s.contractor_signed_at ? formatMediumDate(s.contractor_signed_at) : '—'}
                                </td>
                                <td class="text-end">
                                    ${
                                        s.has_document
                                            ? html`<button class="btn btn-outline-secondary btn-sm"
                                                    @click="${() => this._download(s)}">
                                                <i class="bi bi-download"></i> Download
                                            </button>`
                                            : html`<span class="text-muted">—</span>`
                                    }
                                </td>
                            </tr>
                        `,
                        )}
                    </tbody>
                </table>
            `
            }
        `;
    }
}

customElements.define('sows-view', SOWsView);
